import { useState, useEffect, useMemo } from 'react';
import {
  Building2, Tag, ChevronDown, ChevronRight, Edit3, Save, X, Loader2,
  CheckCircle, AlertCircle, Plus, Trash2, Search, ChevronsDownUp, ChevronsUpDown, Upload,
  UploadCloud, FileText,
} from 'lucide-react';
import { adminApi, NganhHang, LoaiSanPham } from '../services/adminApi';

function normalizeVN(s: string): string {
  return (s || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .trim();
}

type EditableLoai = LoaiSanPham & { _isNew?: boolean };

export default function PhiCoDinh() {
  const [list, setList] = useState<NganhHang[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loaiShop, setLoaiShop] = useState<'mall' | 'regular'>('mall');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingData, setEditingData] = useState<EditableLoai[]>([]);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [search, setSearch] = useState('');
  const [addingOpen, setAddingOpen] = useState(false);
  const [newTen, setNewTen] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importClear, setImportClear] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3000);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await adminApi.getNganhHang();
      if (res.success && res.data) setList(res.data);
    } catch (err) {
      showMessage('error', (err as Error).message || 'Không thể tải dữ liệu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const byShop = list.filter((n) => n.loaiShop === loaiShop);
    const q = normalizeVN(search);
    if (!q) return byShop;
    return byShop.filter(
      (n) =>
        normalizeVN(n.tenNganh).includes(q) ||
        n.loaiSanPham.some(
          (l) => normalizeVN(l.tenLoai).includes(q) || (l.loaiCon || []).some((c) => normalizeVN(c.tenLoai).includes(q))
        )
    );
  }, [list, search, loaiShop]);

  const allCollapsed = filtered.length > 0 && filtered.every((n) => !expanded[n._id]);
  const toggleAll = () => {
    if (allCollapsed) setExpanded(Object.fromEntries(filtered.map((n) => [n._id, true])));
    else setExpanded({});
  };

  const startEdit = (n: NganhHang) => {
    setEditingId(n._id);
    setEditingData(JSON.parse(JSON.stringify(n.loaiSanPham)));
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditingData([]);
  };

  const setLoaiField = (i: number, field: 'tenLoai' | 'phiHoaHong', v: string) =>
    setEditingData((prev) => prev.map((l, idx) => (idx === i ? { ...l, [field]: v } : l)));
  const setSubField = (pi: number, ci: number, field: 'tenLoai' | 'phiHoaHong', v: string) =>
    setEditingData((prev) =>
      prev.map((l, idx) =>
        idx === pi ? { ...l, loaiCon: (l.loaiCon || []).map((c, cidx) => (cidx === ci ? { ...c, [field]: v } : c)) } : l
      )
    );
  const addLoai = () => setEditingData((prev) => [...prev, { tenLoai: '', phiHoaHong: '0%', _isNew: true }]);
  const removeLoai = (i: number) => setEditingData((prev) => prev.filter((_, idx) => idx !== i));

  const saveEdit = async (id: string) => {
    try {
      setSaving(true);
      const clean = editingData.map(({ _isNew, ...rest }) => rest);
      const res = await adminApi.updateNganhHang(id, { loaiSanPham: clean });
      if (res.success) {
        showMessage('success', 'Đã lưu thay đổi');
        cancelEdit();
        fetchData();
      } else showMessage('error', res.message || 'Lỗi khi lưu');
    } catch (err) {
      showMessage('error', (err as Error).message || 'Lỗi khi lưu');
    } finally {
      setSaving(false);
    }
  };

  const createNganh = async () => {
    if (!newTen.trim()) return;
    try {
      setBusy(true);
      const res = await adminApi.createNganhHang({ tenNganh: newTen.trim(), loaiSanPham: [], loaiShop });
      if (res.success) {
        showMessage('success', 'Đã thêm ngành hàng');
        setAddingOpen(false);
        setNewTen('');
        fetchData();
      } else showMessage('error', res.message || 'Lỗi');
    } catch (err) {
      showMessage('error', (err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const deleteNganh = async (n: NganhHang) => {
    if (!window.confirm(`Xoá ngành "${n.tenNganh}"?`)) return;
    try {
      const res = await adminApi.deleteNganhHang(n._id);
      if (res.success) {
        showMessage('success', 'Đã xoá');
        fetchData();
      }
    } catch (err) {
      showMessage('error', (err as Error).message);
    }
  };

  const doImportPdf = async () => {
    if (!importFile) return;
    try {
      setBusy(true);
      const res = await adminApi.uploadNganhHangPdf(importFile, { clearExisting: importClear, loaiShop });
      if (res.success) {
        const extra = res.skippedRows ? ` (bỏ qua ${res.skippedRows} dòng)` : '';
        showMessage('success', `Import thành công ${res.totalParsed ?? ''} ngành${extra}`);
        setImportOpen(false);
        setImportFile(null);
        fetchData();
      } else {
        showMessage('error', res.message || 'Import lỗi');
      }
    } catch (err) {
      showMessage('error', (err as Error).message || 'Import lỗi');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-container wide">
      <div className="page-head">
        <div>
          <h2 className="page-title">Quản Lý Phí Cố Định VN</h2>
          <p className="page-subtitle">Phí hoa hồng theo ngành hàng Shopee</p>
        </div>
        <div className="row" style={{ flexWrap: 'wrap' }}>
          <div className="toggle-group">
            <button className={`toggle-btn${loaiShop === 'mall' ? ' active' : ''}`} onClick={() => setLoaiShop('mall')}>
              <Building2 size={16} /> Shop Mall
            </button>
            <button className={`toggle-btn${loaiShop === 'regular' ? ' active' : ''}`} onClick={() => setLoaiShop('regular')}>
              <Tag size={16} /> Shop Thường
            </button>
          </div>
          <button className="btn btn-secondary" onClick={() => setImportOpen(true)}>
            <Upload size={16} /> Import PDF
          </button>
          <button className="btn btn-primary" onClick={() => setAddingOpen(true)}>
            <Plus size={16} /> Thêm ngành
          </button>
        </div>
      </div>

      {message && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'}`}>
          {message.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          {message.text}
        </div>
      )}

      {/* Toolbar */}
      <div className="row" style={{ marginBottom: 16, gap: 12 }}>
        <div className="search-wrap">
          <Search size={18} className="search-icon" />
          <input
            className="input"
            placeholder="Tìm ngành / loại sản phẩm (không dấu cũng được)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {filtered.length > 0 && !search && (
          <button className="btn btn-secondary" onClick={toggleAll}>
            {allCollapsed ? <ChevronsUpDown size={16} /> : <ChevronsDownUp size={16} />}
            {allCollapsed ? 'Mở tất cả' : 'Thu nhỏ'}
          </button>
        )}
      </div>

      {loading ? (
        <div className="center-screen">
          <Loader2 size={32} className="spin" style={{ color: 'var(--red-600)' }} />
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          {filtered.length === 0 ? (
            <div className="empty-state">
              <Tag size={40} style={{ opacity: 0.4 }} />
              <p>{search ? `Không tìm thấy "${search}"` : `Chưa có ngành cho ${loaiShop === 'mall' ? 'Shop Mall' : 'Shop Thường'}`}</p>
            </div>
          ) : (
            filtered.map((n) => {
              const isOpen = search ? true : !!expanded[n._id];
              const editing = editingId === n._id;
              return (
                <div className="accordion-item" key={n._id}>
                  <div className="accordion-head" onClick={() => setExpanded((e) => ({ ...e, [n._id]: !e[n._id] }))}>
                    <div className="row">
                      {isOpen ? <ChevronDown size={18} className="muted" /> : <ChevronRight size={18} className="muted" />}
                      <span style={{ fontWeight: 600, color: 'var(--slate-800)' }}>{n.tenNganh}</span>
                      <span className="muted" style={{ fontSize: '0.78rem' }}>({n.loaiSanPham.length} loại)</span>
                    </div>
                    <div className="row" onClick={(e) => e.stopPropagation()}>
                      {editing ? (
                        <>
                          <button className="btn-icon save" onClick={() => saveEdit(n._id)} disabled={saving} title="Lưu">
                            {saving ? <Loader2 size={16} className="spin" /> : <Save size={16} />}
                          </button>
                          <button className="btn-icon cancel" onClick={cancelEdit} title="Huỷ">
                            <X size={16} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button className="btn-icon edit" onClick={() => startEdit(n)} title="Sửa">
                            <Edit3 size={16} />
                          </button>
                          <button className="btn-icon danger" onClick={() => deleteNganh(n)} title="Xoá ngành">
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {isOpen && (
                    <div className="accordion-body">
                      {editing ? (
                        <div className="stack" style={{ gap: 12 }}>
                          {editingData.map((loai, i) => (
                            <div className="info-box" key={i}>
                              <div className="row">
                                <input
                                  className="input"
                                  value={loai.tenLoai}
                                  placeholder="Tên loại sản phẩm"
                                  onChange={(e) => setLoaiField(i, 'tenLoai', e.target.value)}
                                />
                                <input
                                  className="input"
                                  style={{ width: 100 }}
                                  value={loai.phiHoaHong}
                                  placeholder="12,50%"
                                  onChange={(e) => setLoaiField(i, 'phiHoaHong', e.target.value)}
                                />
                                <button className="btn-icon danger" onClick={() => removeLoai(i)} title="Xoá loại">
                                  <Trash2 size={16} />
                                </button>
                              </div>
                              {loai.loaiCon && loai.loaiCon.length > 0 && (
                                <div className="stack" style={{ gap: 6, marginTop: 8 }}>
                                  {loai.loaiCon.map((sub, ci) => (
                                    <div className="sub-row" key={ci}>
                                      <span className="muted">└</span>
                                      <input
                                        className="input"
                                        value={sub.tenLoai}
                                        onChange={(e) => setSubField(i, ci, 'tenLoai', e.target.value)}
                                      />
                                      <input
                                        className="input"
                                        style={{ width: 100 }}
                                        value={sub.phiHoaHong}
                                        onChange={(e) => setSubField(i, ci, 'phiHoaHong', e.target.value)}
                                      />
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                          <button className="add-link" style={{ alignSelf: 'flex-start' }} onClick={addLoai}>
                            <Plus size={16} /> Thêm loại sản phẩm
                          </button>
                        </div>
                      ) : (
                        <div className="stack" style={{ gap: 8 }}>
                          {n.loaiSanPham.map((loai, i) => (
                            <div key={i} style={{ borderBottom: '1px solid var(--slate-100)', paddingBottom: 6 }}>
                              <div className="row-between">
                                <span style={{ color: 'var(--slate-800)' }}>{loai.tenLoai}</span>
                                {(!loai.loaiCon || loai.loaiCon.length === 0) && (
                                  <span className="badge badge-green">{loai.phiHoaHong}</span>
                                )}
                              </div>
                              {loai.loaiCon && loai.loaiCon.length > 0 && (
                                <div className="stack" style={{ gap: 4, marginTop: 4 }}>
                                  {loai.loaiCon.map((sub, ci) => (
                                    <div className="sub-row row-between" key={ci}>
                                      <span className="muted" style={{ fontSize: '0.83rem' }}>└ {sub.tenLoai}</span>
                                      <span className="badge badge-green" style={{ fontSize: '0.72rem' }}>{sub.phiHoaHong}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Modal: thêm ngành */}
      {addingOpen && (
        <div className="modal-overlay" onClick={() => setAddingOpen(false)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3>Thêm ngành hàng ({loaiShop === 'mall' ? 'Mall' : 'Thường'})</h3>
              <button className="btn-icon" onClick={() => setAddingOpen(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="field">
                <label className="field-label">Tên ngành hàng</label>
                <input className="input" value={newTen} onChange={(e) => setNewTen(e.target.value)} placeholder="VD: Thời Trang Nam" />
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn btn-secondary" onClick={() => setAddingOpen(false)}>Huỷ</button>
              <button className="btn btn-primary" onClick={createNganh} disabled={busy}>
                {busy ? <Loader2 size={18} className="spin" /> : null} Thêm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: import PDF */}
      {importOpen && (
        <div className="modal-overlay" onClick={() => setImportOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3>Import ngành hàng (PDF) — {loaiShop === 'mall' ? 'Mall' : 'Thường'}</h3>
              <button className="btn-icon" onClick={() => setImportOpen(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <p className="page-subtitle" style={{ marginBottom: 14 }}>
                Tải lên file PDF <b>"Phí cố định theo ngành hàng"</b> của Shopee — hệ thống tự đọc và nhập vào{' '}
                {loaiShop === 'mall' ? 'Shop Mall' : 'Shop Thường'}.
              </p>

              <label
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  const f = e.dataTransfer.files?.[0];
                  if (f && f.type === 'application/pdf') setImportFile(f);
                }}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  gap: 10, padding: '30px 20px', borderRadius: 14, cursor: 'pointer', textAlign: 'center',
                  border: `2px dashed ${dragOver ? 'var(--red-600)' : 'var(--slate-300)'}`,
                  background: dragOver ? '#fef2f2' : 'var(--slate-50)',
                  transition: 'border-color .15s, background .15s',
                }}
              >
                <div style={{
                  width: 54, height: 54, borderRadius: '50%',
                  background: importFile ? '#dcfce7' : '#fee2e2',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {importFile
                    ? <FileText size={26} style={{ color: '#16a34a' }} />
                    : <UploadCloud size={26} style={{ color: 'var(--red-600)' }} />}
                </div>
                {importFile ? (
                  <>
                    <span style={{ fontWeight: 600, color: 'var(--slate-800)', wordBreak: 'break-all' }}>{importFile.name}</span>
                    <span className="muted" style={{ fontSize: '0.8rem' }}>Bấm để chọn file khác</span>
                  </>
                ) : (
                  <>
                    <span style={{ fontWeight: 600, color: 'var(--slate-700)' }}>Kéo thả hoặc bấm để chọn file PDF</span>
                    <span className="muted" style={{ fontSize: '0.8rem' }}>Chỉ nhận file .pdf (tối đa 10MB)</span>
                  </>
                )}
                <input
                  type="file"
                  accept="application/pdf,.pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                />
              </label>

              <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 8, marginTop: 14, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={importClear}
                  onChange={(e) => setImportClear(e.target.checked)}
                  style={{ width: 16, height: 16, flexShrink: 0, margin: 0, accentColor: 'var(--red-600)' }}
                />
                <span>Xoá data cũ của {loaiShop === 'mall' ? 'Mall' : 'Thường'} trước khi import</span>
              </label>
            </div>
            <div className="modal-foot">
              <button className="btn btn-secondary" onClick={() => setImportOpen(false)}>Huỷ</button>
              <button className="btn btn-primary" onClick={doImportPdf} disabled={busy || !importFile}>
                {busy ? <Loader2 size={18} className="spin" /> : <Upload size={16} />} Import
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
